import { Component, computed, inject } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { SonaUsageFacade } from '@sona/angular-auth-domain';
import { BrnProgressComponent, BrnProgressIndicatorComponent } from '@spartan-ng/brain/progress';
import { HlmButtonDirective } from '@spartan-ng/ui-button-helm';
import { HlmProgressDirective, HlmProgressIndicatorDirective } from '@spartan-ng/ui-progress-helm';
import { HlmSkeletonComponent } from '@spartan-ng/ui-skeleton-helm';

@Component({
    selector: 'sona-credits-balance',
    imports: [
        TranslatePipe,
        BrnProgressComponent,
        BrnProgressIndicatorComponent,
        HlmProgressDirective,
        HlmProgressIndicatorDirective,
        HlmSkeletonComponent,
        HlmButtonDirective
    ],
    template: `
        @defer (when !usageFacade.isLoading()) {
            <!-- Disabled until purchasing the credits add-on is implemented -->
            <a
                hlmBtn
                class="tw-flex-col tw-items-start tw-justify-end tw-gap-0.5 tw-px-2 tw-min-w-40 disabled:tw-opacity-100 aria-disabled:tw-opacity-100"
                variant="ghost"
                aria-disabled="true"
            >
                @if (usageInfo(); as usageInfo) {
                    <div>
                        <span class="tw-font-medium">
                            {{ usageInfo.creditsRemaining >= 0 ? usageInfo.creditsRemaining.toLocaleString() : 0 }}
                        </span>
                        / {{ usageInfo.credits.toLocaleString() }} {{ 'PAYMENTS.COMPONENT.CREDITS_BALANCE.CREDITS_LEFT' | translate: { credits: usageInfo.creditsRemaining } }}
                    </div>
                    <div class="tw-w-full">
                        <brn-progress
                            hlm
                            [variant]="usageProgressColor()"
                            [value]="usageInfo.creditsRemaining"
                            [max]="usageInfo.credits"
                            [attr.aria-label]="'PAYMENTS.COMPONENT.CREDITS_BALANCE.ARIA_LABEL' | translate"
                        >
                            <brn-progress-indicator hlm />
                        </brn-progress>
                    </div>
                }
            </a>
        } @placeholder {
            <hlm-skeleton class="tw-h-8 tw-w-48" />
        }
    `
})
export class CreditsBalanceComponent {
    protected readonly usageFacade = inject(SonaUsageFacade);

    readonly usageInfo = computed(() => {
        const usage = this.usageFacade.usage();
        const limits = this.usageFacade.limits();

        if (!usage || !limits) {
            return undefined;
        }

        const credits = limits.credits ?? 0;

        return {
            credits,
            creditsUsed: usage.creditsUsed,
            creditsRemaining: credits - usage.creditsUsed,
            percentUsed: credits === 0 ? 1 : usage.creditsUsed / credits
        };
    });

    readonly usageProgressColor = computed(() => {
        const usageInfo = this.usageInfo();

        if (!usageInfo) {
            return 'blue';
        }

        if (usageInfo.percentUsed >= 0.9) {
            return 'red';
        } else if (usageInfo.percentUsed >= 0.75) {
            return 'amber';
        }

        return 'blue';
    });
}
